import { OAUTH_METHODS, type OAuthMethod, RequiredDataStep, SupportedLanguage, type UrlsType } from './types';
import { Competitor, type Path, type RouteId } from './route-types';

let LOCALE = SupportedLanguage.English;

export function changeRouteLocale(locale: SupportedLanguage) {
    LOCALE = locale;
}

const urlRoom = {
    isRoom: (routeId: string) => routeId.endsWith('/room/[id]'),
    room: (id?: string, params?: Record<string, string>) => id ? build('/room/[id]', params, null, { id }) : build('/room'),
    roomSearch: (params?: Record<string, string>) => build('/room/search', params),
    roomStart: (host?: string, params?: Record<string, string>) => buildFullPath('/room/start', host, params),
    roomCall: (params?: Record<string, string>) => build('/room/call', params),
    roomSettings: () => build('/room/settings'),
};

const urlSignup = {
    login: () => build('/login'),
    signup: () => build('/signup'),
    signupRequired: (step: RequiredDataStep, params?: Record<string, string>) => {
        if (step === RequiredDataStep.Step7) {
            return build(step, params);
        } else {
            return build(`/signup${step}`, params);
        }
    },
    signupVerify: () => '/signup/verify', // no locale
    loginOAuthVerify: (oauthMethod: OAuthMethod, host?: string) => {
        return buildFullPath(`/login/[oauthMethod]/verify`, host, null, null, { oauthMethod });
    },
    loginOAuthFailure: (oauthMethod: OAuthMethod, host?: string, params?: Record<string, string | number>) => {
        return buildFullPath('/login/[oauthMethod]/failure', host, params, null, { oauthMethod });
    },
    signupOAuthSuccess: (oauthMethod: OAuthMethod, host?: string, params?: Record<string, string | number>) => {
        return buildFullPath('/signup/[oauthMethod]/success', host, params, null, { oauthMethod });
    },
    signupOAuthFailure: (oauthMethod: OAuthMethod, host?: string, params?: Record<string, string | number>) => {
        return buildFullPath(`/signup/[oauthMethod]/failure`, host, params, null, { oauthMethod });
    },
};

const urlStory = {
    storyUpload: () => build('/story/upload'),
    storyEdit: () => build('/story/edit'),
    storyClose: () => {
        const url = new URL(window.location.href);
        url.hash = '';
        return url.href;
    },
    storyOpen: (storyId?: string, baseUrl?: string) => {
        let newBaseUrl = baseUrl;
        if (newBaseUrl === undefined) {
            const url = new URL(baseUrl || window.location.href);
            url.hash = '';
            newBaseUrl = url.href;
        }
        return `${newBaseUrl}#/stories/${storyId ? storyId : ''}`;
    }
}

const urlMessages = {
    isMessage: (routeId: string) => routeId.endsWith('/messages/[id]'),
    myMessages: (host?: string, params?: Record<string, string | number>) =>
        buildFullPath('/my/messages', host, params),
    messages: (id: string) => build('/messages/[id]', null, null, { id }),
}

const urlProfile = {
    favorites: () => build('/favorites'),
    chatHistory: () => build('/chat-history'),
    profile: () => build('/profile'),
    profileSubscription: () => build('/profile/subscription'),
    profileChangePassword: () => build('/profile/change-password'),
    profileDeactivate: () => build('/profile/deactivate'),
    profilePaymentDetails: () => build('/profile/payment-details'),
};

const urlList = {
    sessionExists: () => build('/session-exists'),
    index: (host?: string, params?: Record<string, string | number>) => buildFullPath('', host, params),
    talkToStrangers: (host?: string, params?: Record<string, string | number>) => {
        return buildFullPath('/talk-to-strangers', host, params);
    },
    chatRules: (host?: string, params?: Record<string, string | number>) => {
        return buildFullPath('/chat-rules', host, params);
    },
    sitemap: () => build('/alternative'),
    alternative: (competitor: Competitor, host?: string) => {
        return buildFullPath('/alternative/[competitor]', host).replace('[competitor]', competitor);
    },
    referralLink: (refCode: string, host?: string) => buildFullPath('/id[refCode]', host).replace('[refCode]', refCode),
    unauthorizedStart: () => buildFullPath('/start'),
    statement2257: () => build('/info/2257'),
    termsOfUse: (host?: string, params?: Record<string, string | number>) => buildFullPath('/terms', host, params),
    privacyPolicy: (host?: string) => buildFullPath('/privacy', host),
    privacyNotice: () => '/privacy-notice', // ! no need local
    reportContent: () => build('/report-content'),
    resetPassword: () => build('/reset-password'),
    resetPasswordSuccess: () => build('/reset-password/success'),
    resetPasswordExpired: () => build('/reset-password/expired'),
    paymentSuccess: (params?: Record<string, string | number>) => {
        return buildFullPath('/payment-success', undefined, params);
    },
    paymentFailure: (host?: string, params?: Record<string, string | number>) => {
        return buildFullPath('/payment-failure', host, params);
    },
    paymentFailed: (host?: string, params?: Record<string, string | number>) => {
        return buildFullPath('/payment-failed', host, params);
    },
    block: () => build('/block'),
    unsupportedIOSDevice: () => build('/unsupported-device'),
    unsupportedBrowser: () => build('/unsupported-browser'),
    unstableConnection: () => build('/unstable-connection'),
    mailSubscriptionUnsubscribe: (host?: string, params?: Record<string, string | number>) => {
        return buildFullPath('/mail-subscription/unsubscribe', host, params);
    },
    referral: (host?: string) => buildFullPath('/referral', host),
    myIncome: () => build('/my-income'),
    ...urlSignup,
    ...urlRoom,
    ...urlProfile,
    ...urlMessages,
    ...urlStory,
};

export function clearParams<T extends Record<string, string | number | boolean>>(
    params: T
): T {
    const cleanParams: Record<string, string | number> = {};
    Object.entries(params).forEach(([key, value]) => {
        if (value && (key !== 'page' || value !== 1)) {
            cleanParams[key] = `${value}`;
        }
    });
    return cleanParams as T;
}

export function applyToSearchParams<T extends Record<string, string[] | string | number | null | boolean>>(
    searchParams: URLSearchParams,
    params: T
): T {
    const cleanParams: Record<string, string[] | string | number> = {};
    [...searchParams.entries()].forEach(([key]) => {
        searchParams.delete(key);
    });
    Object.entries(params).forEach(([key, value]) => {
        if (value && Array.isArray(value)) {
            value.forEach((v, index) => {
                searchParams.append(`${key}[${index}]`, `${v}`);
            });
            cleanParams[key] = value;
        } else if (value && (key !== 'page' || value !== 1)) {
            cleanParams[key] = `${value}`;
            searchParams.set(key, `${value}`);
        }
    });
    return cleanParams as T;
}

export function getHrefLocale(currentUrl: URL): SupportedLanguage {
    const parts = currentUrl.pathname.split('/');
    if (parts.length <= 1 || !Object.values(SupportedLanguage).includes(parts[1] as SupportedLanguage)) {
        return SupportedLanguage.English;
    }
    return parts[1] as SupportedLanguage;
}

export function getHref(pageUrl: URL, locale: SupportedLanguage | null = null): string {
    const url = new URL('/', pageUrl);
    url.pathname = build(pageUrl.pathname as Path<RouteId>, {}, locale);
    return url.href;
}

export function removeLocale(url: URL): URL {
    const newUrl = new URL(url);
    // remove locale prefix
    const parts = newUrl.pathname.split('/');
    if (Object.values(SupportedLanguage).includes(parts[1] as SupportedLanguage)) {
        parts.splice(1, 1);
        newUrl.pathname = parts.join('/');
    }
    return newUrl;
}

export function setUrlLocale(locale: SupportedLanguage, currentUrl: URL, leaveSearch = false): string {
    const url = removeLocale(currentUrl);

    if (!leaveSearch) {
        url.search = '';
        url.hash = '';
    }

    // add locale prefix
    if (SupportedLanguage.English !== locale) {
        url.pathname = `/${locale}${url.pathname}`;
    }

    return url.href;
}

export function buildAlternateLinks(page: URL): Map<string, string> {
    const hrefs: Map<string, string> = new Map();

    for (const locale of Object.values(SupportedLanguage)) {
        const localizedPath = getHref(page, locale);
        if (localizedPath) {
            hrefs.set(locale, localizedPath);
        }
    }

    return hrefs;
}

export function build<T extends Path<RouteId>>(
    path: T,
    params?: Record<string, string[] | string | number | null | boolean> | null,
    locale: SupportedLanguage | null = null,
    pathParams?: Record<string, string>
) {
    const newLocale = locale || LOCALE;
    const url = new URL('https://localhost');
    let parts = (path as string).split('/').filter(p => p);
    if (parts[0] === '' || Object.values(SupportedLanguage).includes(parts[0] as SupportedLanguage)) {
        // remove locale for now
        parts.splice(0, 1);
    }
    if (SupportedLanguage.English !== newLocale) {
        parts.unshift(newLocale);
    }
    if (pathParams) {
        parts = parts.map(f => pathParams[f.substring(1, f.length - 1)]
            ? pathParams[f.substring(1, f.length - 1)]
            : f
        )
    }
    if (!params) {
        return `/${parts.join('/')}`;
    }
    applyToSearchParams(url.searchParams, params);
    const query = url.searchParams.toString();
    if (!query) {
        return `/${parts.join('/')}`;
    }
    return `/${parts.join('/')}?${query}`;
}

const URLS: UrlsType<typeof urlList> = urlList;

export const strictUnAuthUrls = () => [
    URLS.signup(),
    URLS.index(),
    URLS.talkToStrangers(),
    URLS.login(),
    URLS.unauthorizedStart(),
    ...OAUTH_METHODS.map(m => URLS.loginOAuthFailure(m)),
    ...OAUTH_METHODS.map(m => URLS.loginOAuthVerify(m)),
    ...OAUTH_METHODS.map(m => URLS.signupOAuthFailure(m)),
    ...Object.values(Competitor).map(c => URLS.alternative(c)),
];

export const unsupportedDeviceUrls = () => [
    URLS.unsupportedIOSDevice(),
    URLS.termsOfUse(),
    URLS.privacyPolicy(),
];

export const operatorUrls = () => [
    URLS.referral(),
    URLS.profilePaymentDetails(),
    URLS.storyUpload(),
    URLS.chatRules(),
    URLS.myIncome(),
];

export const userUrls = () => [
    URLS.profileSubscription(),
];

export const landingPages = () => [
    URLS.index(),
    URLS.talkToStrangers(),
    ...(Object.values(Competitor).map(c => URLS.alternative(c))),
    URLS.sitemap(),
];

export const isAlternativeLandingPage = (path: string) => {
    return Object.values(Competitor).map(c => URLS.alternative(c)).includes(path);
}

export const isPaymentPage = () => {
    return [
        '/payment-failure',
        '/payment-failed',
        '/payment-success'
    ].some((path) => window.location.href.includes(path));
};

export function buildFullPath<T extends Path<RouteId>>(
    path: T,
    host?: string,
    params?: Record<string, string | number | boolean> | null,
    locale: SupportedLanguage | null = null,
    pathParams?: Record<string, string>
) {
    const start = host ?? '';
    return `${start}${build(path, params, locale, pathParams)}`;
}

// it is required so stories open on an anchor
export function reloadPage() {
    const url = new URL(window.location.href);
    url.hash = '';
    window.location.href = url.href
}

export { URLS };
